import { createFileRoute, Link, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { KeyRound, LogOut } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useLogoutMutation } from '@/features/auth/api';
import { BrandLogo } from '@/shared/branding';
import { PoweredBy } from '@/shared/components/PoweredBy';
import { Button, IconButton } from '@/shared/components/ui';
import { useAuthStore } from '@/shared/stores/authStore';

export const Route = createFileRoute('/user')({
  beforeLoad: () => {
    const state = useAuthStore.getState();
    if (!state.isAuthenticated || state.user?.role !== 'delegated') {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router contract: beforeLoad throws a redirect object.
      throw redirect({ to: '/user/login' });
    }
  },
  component: UserShell,
});

function UserShell() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const logout = useLogoutMutation();

  // If the session is cleared while the portal is open — e.g. an admin bans the
  // delegate and their next request 401s + refresh fails — bounce to login
  // immediately instead of leaving them on a dead page.
  useEffect(() => {
    if (!isAuthenticated) void navigate({ to: '/user/login' });
  }, [isAuthenticated, navigate]);

  const signOut = () => {
    void logout.mutateAsync().finally(() => void navigate({ to: '/user/login' }));
  };

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-foreground">
      <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-border bg-surface-glass px-4 backdrop-blur-xl sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <BrandLogo className="max-h-10 w-auto max-w-[9rem] object-contain" />
          {user?.units?.length ? (
            <span className="truncate rounded-full bg-brand-soft px-2.5 py-1 text-xs font-medium text-brand">
              {t('portal.unit')} {user.units.map((u) => u.unitNumber).join('، ')}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Link to="/user/change-password">
            <IconButton label={t('auth.user.changePassword.title')} variant="secondary" size="sm">
              <KeyRound />
            </IconButton>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            startIcon={<LogOut className="size-4" />}
            onClick={signOut}
          >
            {t('portal.signOut')}
          </Button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-6 sm:px-6">
        <Outlet />
      </main>

      <footer className="border-t border-border bg-surface px-4 py-3 sm:px-6">
        <div className="mx-auto flex w-full max-w-3xl justify-center">
          <PoweredBy />
        </div>
      </footer>
    </div>
  );
}
